import type { StaffImportJobDetail } from "@lafayette/schemas";
import { forwardRef } from "react";
import { Link } from "react-router";
import { factList, groupBox, groupLegend, hintText } from "../../catalog/components/styles";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { SelectField, TextField } from "../../ui/fields";
import { Notice } from "../../ui/notice";
import { StatusMessage } from "../../ui/status-message";
import type { SubmitBlocker } from "../blockers";
import type { SaveMessage } from "../commands";
import { JobStatusBadge } from "../components/parts";
import { StepCard } from "../components/StepCard";
import { linkButton, stepActions } from "../components/styles";
import { describeJobStatus } from "../labels";
import { jobAddress, jobPhase } from "../review/progress";
import {
  DEFAULT_SOURCE_SYSTEM,
  type ValidationSettingsForm,
  type ValidationSettingsIssues,
} from "../validation-settings";

type SaveStepProps = Readonly<{
  detail: StaffImportJobDetail | null;
  countsText: string;
  itemTypeText: string;
  dirty: boolean;
  /** The job is not in MAPPING any more. */
  readOnly: boolean;
  blockers: readonly SubmitBlocker[];
  /** Staff tried to submit: the blockers are announced at once. */
  attempted: boolean;
  message: SaveMessage | null;
  pending: boolean;
  onSave: () => void;
  onSubmit: () => void;
  onGoToColumn: (columnIndex: number) => void;
  onCancelDraft: () => void;
  onStartOver: () => void;
  settings: ValidationSettingsForm;
  settingsIssues: ValidationSettingsIssues;
  onSettings: (settings: ValidationSettingsForm) => void;
}>;

const dateOrderOptions = [
  { value: "ISO_ONLY", label: "Refuse an unclear numeric date (default)" },
  { value: "DMY", label: "Day first (31/12/2027)" },
  { value: "MDY", label: "Month first (12/31/2027)" },
];

const stateNotice = (detail: StaffImportJobDetail): string => {
  switch (detail.job.status) {
    case "REVIEW_REQUIRED":
      return "The rows were checked. This import waits for your review. The mapping can no longer be changed, and nothing has reached the catalog.";
    case "APPROVED":
    case "PROCESSING":
      return "This import was approved and is being processed. The mapping can no longer be changed.";
    case "COMPLETED":
      return "This import finished. The mapping can no longer be changed.";
    default:
      break;
  }
  const view = describeJobStatus(detail.job);
  switch (view.tone) {
    case "submitted":
      return "This import was submitted. It is waiting for validation. The mapping can no longer be changed, and nothing has reached the catalog.";
    case "cancelled":
      return "This draft was cancelled. It can no longer be changed, and nothing has reached the catalog.";
    case "failed":
      return "This import failed. It can no longer be changed.";
    default:
      return "This import can no longer be changed.";
  }
};

/**
 * Step 5. Save the draft, submit it for validation, or cancel it. Submit is blocked, with the reasons
 * listed, while the draft is unsaved, has unsaved changes, or has a column that is not mapped. The
 * button uses `aria-disabled`, so a staff member who presses it still hears why it did not run.
 */
export const SaveStep = forwardRef<HTMLFieldSetElement, SaveStepProps>(function SaveStep(
  {
    detail,
    countsText,
    itemTypeText,
    dirty,
    readOnly,
    blockers,
    attempted,
    message,
    pending,
    onSave,
    onSubmit,
    onGoToColumn,
    onCancelDraft,
    onStartOver,
    settings,
    settingsIssues,
    onSettings,
  },
  blockersRef,
) {
  const blocked = blockers.length > 0;
  const hasJob = detail !== null;

  return (
    <StepCard step="save" title="Save">
      <dl className={factList}>
        <div>
          <dt>Columns</dt>
          <dd>{countsText}</dd>
        </div>
        <div>
          <dt>Item type for every row</dt>
          <dd>{itemTypeText}</dd>
        </div>
        <div>
          <dt>Draft</dt>
          <dd>
            {hasJob ? (
              <>
                <JobStatusBadge job={detail.job} />
                {detail.mapping === null ? null : ` Mapping revision ${detail.mapping.revision}.`}
                {dirty ? " You have unsaved changes." : ""}
              </>
            ) : (
              "Not saved yet."
            )}
          </dd>
        </div>
      </dl>

      {readOnly && hasJob ? (
        <>
          <Notice tone="info" role="status">
            {stateNotice(detail)}
          </Notice>
          {jobPhase(detail.job) === "closed" ? null : (
            <div className={stepActions}>
              <Button asChild>
                <Link to={jobAddress(detail.job)}>Open the review</Link>
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <p>
            Saving keeps your choices as a draft. Submitting moves the draft to validation. Neither
            changes the catalog.
          </p>
          {hasJob ? (
            <fieldset className={cn(groupBox, "gap-4")}>
              <legend className={groupLegend}>Validation settings</legend>
              <p className={hintText}>
                These are optional. They tell the server how to read the rows when you submit.
                Without a default time zone, every event whose file has no time zone needs a
                confirmation of the zone the server guesses from the city.
              </p>
              <TextField
                id="import-setting-time-zone"
                label="Default time zone"
                hint="An IANA name, for example Asia/Kolkata. Used for rows that have no time zone."
                value={settings.defaultTimeZone}
                error={settingsIssues.defaultTimeZone}
                onChange={(value) => onSettings({ ...settings, defaultTimeZone: value })}
              />
              <TextField
                id="import-setting-source-system"
                label="Source system"
                hint={`Names where the rows come from, for example legacy_events_2024. Default: ${DEFAULT_SOURCE_SYSTEM}. Two sheets with numbers as ids need different names.`}
                value={settings.sourceSystem}
                error={settingsIssues.sourceSystem}
                onChange={(value) => onSettings({ ...settings, sourceSystem: value })}
              />
              <SelectField
                id="import-setting-date-order"
                label="Date order"
                hint="How a numeric date such as 03/04/2027 is read. By default it is refused as unclear."
                value={settings.dateOrder}
                options={dateOrderOptions}
                onChange={(value) =>
                  onSettings({
                    ...settings,
                    dateOrder: value as ValidationSettingsForm["dateOrder"],
                  })
                }
              />
            </fieldset>
          ) : null}
          <div className={stepActions}>
            <Button
              type="button"
              onClick={onSave}
              disabled={pending || (hasJob && !dirty)}
              aria-describedby={hasJob && !dirty ? "import-save-hint" : undefined}
            >
              {pending ? "Working…" : "Save draft"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              aria-disabled={blocked || pending ? "true" : undefined}
              aria-describedby={blocked ? "import-submit-blockers" : undefined}
              onClick={onSubmit}
            >
              Submit for validation
            </Button>
            {hasJob ? (
              <Button
                type="button"
                variant="destructive"
                disabled={pending}
                onClick={onCancelDraft}
              >
                Cancel draft
              </Button>
            ) : (
              <Button type="button" variant="secondary" disabled={pending} onClick={onStartOver}>
                Start over
              </Button>
            )}
          </div>
          {hasJob && !dirty ? (
            <p id="import-save-hint" className={hintText}>
              There are no changes to save.
            </p>
          ) : null}

          {blocked ? (
            <fieldset
              ref={blockersRef}
              id="import-submit-blockers"
              className="m-0 min-w-0 rounded-sm border border-warning-border bg-warning-soft p-4"
              aria-live={attempted ? "assertive" : "polite"}
              tabIndex={-1}
            >
              <legend className="px-1 font-bold text-warning">Why submit is blocked</legend>
              <ul className="m-0 grid gap-1 pl-5">
                {blockers.map((blocker) => (
                  <li key={blocker.id}>
                    {blocker.columnIndex === null ? (
                      blocker.text
                    ) : (
                      <Button
                        variant="link"
                        className={linkButton}
                        onClick={() => onGoToColumn(blocker.columnIndex as number)}
                      >
                        {blocker.text}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </fieldset>
          ) : null}
        </>
      )}

      <StatusMessage message={message} />
    </StepCard>
  );
});
